// src/prototype/data/adapters/enterpriseSpineMock.ts
// ============================================================
// ENTERPRISE PROTOTYPE — OVER-TIME SPINE · SEED (mock, BQ-shaped rows)
//
// Phase 1.1 seed corrections (data only):
//  - Capital cap is real money: Q1 baseline = $50.0M, Q2 "Budget Reduction" = $40.0M.
//  - Deep portfolio (~174 initiatives). The funded line is NOT a hardcoded top-N — it is
//    driven by cumulative capital_ask vs. the run's cap (the mechanism the Scenarios
//    surface shows). Each initiative carries a capital_ask; the adapter walks rank order
//    and funds until the cap is exhausted (40 funded @ $50M, 33 @ $40M — 7 crossed;
//    counts verified against scenarioModel/reportsModel, settling the prior ~31 note).
//
// The ONLY module that fabricates data; everything is shaped to the BQ row types so a live
// fetch can replace these arrays with no adapter/UI change.
//
// HERO ANCHOR (Option D re-root): the protagonist is Care Coordination and Transition Management
// (uc 1693), matching the storefront/drilldown hero. Its regenerated composite (acme-demo-v1 over
// the authored DIM6 dims) is 82.0; confidence 0.61 (stage confidence is NULL → display constant).
// Hero composite follows a RISING ARC that LANDS on the real 82.0: Q1 76 → Q2 79 → +6mo 82.0.
// Only the LATEST (+6mo) point is pinned to the real 82.0 (must agree with the CORE drilldown/
// portfolio); Q1/Q2 are the admittedly-simulated planning history and are meant to differ. The
// portfolio "as-of" default is set to the LATEST run (see useEnterpriseRun) so the first number
// a partner sees is 82.0. Rank story preserved: hero #2 → #1 → #1 (uc 30, the strategic-
// alignment leader deferred at Q1 per dec-0002, holds Q1 #1 at 77 then the hero overtakes).
// PRESERVED: client 200 (ACME); the 3 runs; the 1 saved scenario; the 8 decision-log entries
// (hero-name references on 1555). Every other initiative's invented trajectory is unchanged
// except uc 30's Q1 (72.9 → 77) which carries the #1-at-Q1 slot the rank story needs.
// ============================================================

import {
  ENTERPRISE_CLIENT_ID,
  type ScoringRun,
  type RunScore,
  type Scenario,
  type DecisionLogEntry,
  type DimensionScores,
  type Owner,
} from './enterpriseSpineTypes';

const CID = ENTERPRISE_CLIENT_ID;

// ---- run ids -----------------------------------------------------------------
export const RUN_Q1 = 'run-2026q1-baseline';
export const RUN_Q2 = 'run-2026q2-budget-reduction';
export const RUN_6MO = 'run-2026-6mo-actual';
export const SCEN_Q2 = 'scen-2026q2-budget-reduction';

// ---- capital caps ($M) -------------------------------------------------------
/** Q1 planning baseline capital cap. A run with no scenario uses this. */
export const BASELINE_BUDGET_CAP = 50.0; // $M
const Q2_BUDGET_CAP = 40.0; // $M — a 20% reduction (−$10M)

// =============================================================================
// FEATURED initiatives — authored per-run composite/confidence + capital_ask.
// These carry the narrative (anchor, crossings, decisions). Positions are driven by
// composite (ranks emerge from sorting), so featured land where the story needs them.
// =============================================================================
interface Featured {
  use_case_id: number;
  title: string;
  owner: Owner;
  category: string;
  profile: DimensionScores;
  capital_ask: number; // $M
  q1: [number, number]; // [composite, confidence]
  q2: [number, number];
  m6: [number, number];
}

// Category constants (the 7 canonical DVA service lines; match the design roster).
const CAT_CONSUMER = 'Consumer and Patient-Centric Solutions';
const CAT_INNOVATION = 'Data-Driven Innovation and Product Development';
const CAT_OPS = 'Operational Excellence Through Data Optimization';
const CAT_PROVIDER = 'Provider Enablement and Local Ecosystem Support';
const CAT_RESEARCH = 'Research & Data Product Collaborations';
const CAT_REVENUE = 'Revenue Growth & Market Expansion';
const CAT_VBC = 'Value-Based Care and Payer Innovation';

// owner+category are MATCHED TO THE DESIGN roster (Enterprise Prototype.dc.html): the anchor
// is the real dva-stage hero uc 1555 = S. Whitmore / PMO / Operational Excellence, and each
// named initiative takes the owner/category the design assigns to the same (or nearest) title.
const FEATURED: Featured[] = [
  // HERO ANCHOR (Option D re-root): uc 1693 Care Coordination and Transition Management. Rising arc
  // landing on the regenerated composite 82.0 — Q1 76 -> Q2 79 -> +6mo 82.0 (only +6mo pinned to the
  // real composite; the as-of default surfaces +6mo). Rank #2 -> #1 -> #1; confidence 0.61 -> 0.82.
  // Profile dims are CC's bridgeTo4 (fin 80 / urg 72 / strat 90 / feas 82).
  { use_case_id: 1693, title: 'Care Coordination and Transition Management', owner: { name: 'S. Whitmore', org: 'PMO' }, category: CAT_VBC, profile: { financial_impact: 80.0, operational_urgency: 72.0, strategic_alignment: 90.0, implementation_feasibility: 82.0 }, capital_ask: 2.1, q1: [76.0, 0.61], q2: [79.0, 0.64], m6: [82.0, 0.82] },
  // uc 30 carries the Q1 #1 slot (72.9 → 77) so the hero rises #2 → #1; it is deferred at Q1
  // (dec-0002) and slips under the Q2 reweighting (lowest feasibility), landing below the hero.
  { use_case_id: 30, title: 'Analytics-as-a-Service (AaaS)', owner: { name: 'S. Whitmore', org: 'PMO' }, category: CAT_PROVIDER, profile: { financial_impact: 70.0, operational_urgency: 63.0, strategic_alignment: 86.0, implementation_feasibility: 44.0 }, capital_ask: 2.2, q1: [77.0, 0.70], q2: [72.1, 0.68], m6: [69.5, 0.66] },
  { use_case_id: 66, title: 'AI-Driven Operational Efficiency Tools', owner: { name: 'L. Grant', org: 'Finance' }, category: CAT_INNOVATION, profile: { financial_impact: 74.0, operational_urgency: 71.0, strategic_alignment: 72.0, implementation_feasibility: 58.0 }, capital_ask: 1.6, q1: [70.7, 0.66], q2: [69.0, 0.64], m6: [70.2, 0.71] },
  { use_case_id: 22, title: 'Remote Patient Monitoring and Virtual Care Solutions', owner: { name: 'J. Novak', org: 'Facilities' }, category: CAT_VBC, profile: { financial_impact: 64.0, operational_urgency: 70.0, strategic_alignment: 76.0, implementation_feasibility: 52.0 }, capital_ask: 1.4, q1: [68.9, 0.63], q2: [66.5, 0.60], m6: [64.8, 0.60] },
  { use_case_id: 23, title: 'Intelligent Patient Engagement and Education', owner: { name: 'A. Flores', org: 'Digital' }, category: CAT_CONSUMER, profile: { financial_impact: 71.0, operational_urgency: 64.0, strategic_alignment: 74.0, implementation_feasibility: 66.0 }, capital_ask: 1.1, q1: [68.4, 0.60], q2: [70.0, 0.66], m6: [69.5, 0.67] },
  // Near-line CROSSER (down): capital-heavy. Its RANK barely moves — it drops below the
  // line purely because its $3.0M ask no longer fits once the cap is cut $50M→$40M.
  { use_case_id: 31, title: 'Integrated Virtual Care and Remote Patient Monitoring', owner: { name: 'A. Flores', org: 'Digital' }, category: CAT_VBC, profile: { financial_impact: 62.0, operational_urgency: 66.0, strategic_alignment: 80.0, implementation_feasibility: 47.0 }, capital_ask: 3.0, q1: [62.6, 0.55], q2: [61.8, 0.53], m6: [60.8, 0.55] },
  // Near-line CROSSER (up): low-cost lever → rises into the funded set under reweighting.
  { use_case_id: 40, title: 'Patient Engagement and Shared Decision-Making', owner: { name: 'A. Flores', org: 'Digital' }, category: CAT_VBC, profile: { financial_impact: 69.0, operational_urgency: 61.0, strategic_alignment: 70.0, implementation_feasibility: 63.0 }, capital_ask: 0.9, q1: [61.0, 0.57], q2: [63.6, 0.62], m6: [64.2, 0.63] },
  { use_case_id: 38, title: 'Precision Medicine Initiatives', owner: { name: 'D. Park', org: 'Research' }, category: CAT_RESEARCH, profile: { financial_impact: 58.0, operational_urgency: 55.0, strategic_alignment: 78.0, implementation_feasibility: 40.0 }, capital_ask: 1.5, q1: [56.0, 0.52], q2: [55.0, 0.51], m6: [55.5, 0.58] },
  { use_case_id: 39, title: 'Smart Facility Management and Energy Optimization', owner: { name: 'J. Novak', org: 'Facilities' }, category: CAT_OPS, profile: { financial_impact: 60.0, operational_urgency: 52.0, strategic_alignment: 55.0, implementation_feasibility: 61.0 }, capital_ask: 0.8, q1: [40.0, 0.50], q2: [39.0, 0.48], m6: [38.0, 0.49] },
];

// =============================================================================
// FILLER initiatives — fill the portfolio out to ~174. Composites descend below the
// featured top cluster so the distribution is smooth; asks/confidence/dimensions are
// deterministic (a use_case_id hash — no Math.random so the seed is stable).
// =============================================================================
const FILLER_COUNT = 165; // 9 featured + 165 = 174
const FILLER_ID_BASE = 101; // ids 101..265 (no collision with featured ids < 100)
const FILLER_TOP = 69.9; // just below uc66 (70.7) so featured hold ranks 1–3
const FILLER_BOTTOM = 26.0;
const FILLER_ASK_BASE = 0.90; // tuned so cumulative asks fund 40 @ $50M / 33 @ $40M (7 crossed)
const FILLER_ASK_SPREAD = 0.50;

const THEMES = [
  'Automated Prior Authorization', 'Sepsis Early-Warning Analytics', 'Length-of-Stay Optimization',
  'Denials Prevention Analytics', 'Supply Chain Demand Forecasting', 'OR Scheduling Optimization',
  'Population Health Risk Stratification', 'Clinical Documentation Improvement', 'Bed Management & Capacity',
  'Pharmacy Spend Optimization', 'No-Show Prediction', 'Care Gap Closure', 'Nurse Staffing Optimization',
  'Revenue Cycle Automation', 'Readmission Risk Modeling', 'Referral Leakage Analytics',
  'Chronic Disease Management', 'Telehealth Utilization Analytics', 'Claims Anomaly Detection',
  'Patient Flow Analytics',
];

/** Deterministic 0–1 hash of an integer (stable across loads; no Math.random). */
const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

interface InitiativeBase {
  use_case_id: number;
  title: string;
  owner: Owner;
  category: string;
  profile: DimensionScores;
  capital_ask: number;
  q1: [number, number];
  q2: [number, number];
  m6: [number, number];
}

// Filler owner/category assignment DRAWN FROM THE DESIGN'S org set. The design assigns its
// filler owners via owners[(i*5)%9] over this exact 9-person roster; categories cycle the 7
// canonical service lines. Deterministic by index so it is stable across loads.
const DESIGN_OWNERS: Owner[] = [
  { name: 'S. Whitmore', org: 'PMO' },
  { name: 'K. Ellison', org: 'PMO' },
  { name: 'R. Vasquez', org: 'Informatics' },
  { name: 'M. Osei', org: 'Informatics' },
  { name: 'A. Flores', org: 'Digital' },
  { name: 'L. Grant', org: 'Finance' },
  { name: 'C. Adeyemi', org: 'Quality' },
  { name: 'J. Novak', org: 'Facilities' },
  { name: 'D. Park', org: 'Research' },
];
const FILLER_CATEGORIES = [CAT_OPS, CAT_PROVIDER, CAT_VBC, CAT_CONSUMER, CAT_INNOVATION, CAT_REVENUE, CAT_RESEARCH];

const FILLERS: InitiativeBase[] = Array.from({ length: FILLER_COUNT }, (_, k) => {
  const use_case_id = FILLER_ID_BASE + k;
  const composite = Math.round((FILLER_TOP - (k * (FILLER_TOP - FILLER_BOTTOM)) / (FILLER_COUNT - 1)) * 10) / 10;
  const conf = Math.round((0.45 + hash01(use_case_id) * 0.35) * 100) / 100;
  const capital_ask = Math.round((FILLER_ASK_BASE + hash01(use_case_id + 7) * FILLER_ASK_SPREAD) * 10) / 10;
  const profile: DimensionScores = {
    financial_impact: Math.round(42 + hash01(use_case_id + 1) * 40),
    operational_urgency: Math.round(40 + hash01(use_case_id + 2) * 40),
    strategic_alignment: Math.round(45 + hash01(use_case_id + 3) * 40),
    implementation_feasibility: Math.round(35 + hash01(use_case_id + 4) * 42),
  };
  return {
    use_case_id,
    title: `${THEMES[k % THEMES.length]} · Initiative ${use_case_id}`,
    owner: DESIGN_OWNERS[(k * 5) % DESIGN_OWNERS.length],
    category: FILLER_CATEGORIES[k % FILLER_CATEGORIES.length],
    profile,
    capital_ask,
    // Fillers hold their score across runs — the Q1→Q2 story is the moved line + featured
    // movers, not portfolio-wide churn.
    q1: [composite, conf], q2: [composite, conf], m6: [composite, conf],
  };
});

const ALL: InitiativeBase[] = [...FEATURED, ...FILLERS];

// ---- exported lookups the adapter needs --------------------------------------
export const INITIATIVE_TITLES: Record<number, string> = Object.fromEntries(ALL.map((i) => [i.use_case_id, i.title]));
/** Owner (name + org / service line) per initiative — matched to the design roster. */
export const INITIATIVE_OWNERS: Record<number, Owner> = Object.fromEntries(ALL.map((i) => [i.use_case_id, i.owner]));
/** Category / service line per initiative — matched to the design roster. */
export const INITIATIVE_CATEGORIES: Record<number, string> = Object.fromEntries(ALL.map((i) => [i.use_case_id, i.category]));
/** capital_ask ($M) per initiative — the cumulative-vs-cap funding line reads this. */
export const CAPITAL_ASKS: Record<number, number> = Object.fromEntries(ALL.map((i) => [i.use_case_id, i.capital_ask]));
const PROFILE: Record<number, DimensionScores> = Object.fromEntries(ALL.map((i) => [i.use_case_id, i.profile]));

// ---- build RunScore rows (rank emerges from composite, ties by use_case_id) ---
type RunKey = 'q1' | 'q2' | 'm6';
function buildRun(run_id: string, key: RunKey): RunScore[] {
  return ALL.map((i) => ({ i, composite: i[key][0], confidence: i[key][1] }))
    .sort((a, b) => b.composite - a.composite || a.i.use_case_id - b.i.use_case_id)
    .map((e, idx) => ({
      run_id,
      use_case_id: e.i.use_case_id,
      rank: idx + 1,
      composite: e.composite,
      confidence: e.confidence,
      dimension_scores: PROFILE[e.i.use_case_id],
    }));
}

// =============================================================================
// SEED
// =============================================================================

export const SCORING_RUNS: ScoringRun[] = [
  { run_id: RUN_Q1, client_id: CID, label: 'Q1 2026 Baseline Planning', created_at: '2026-01-29T00:00:00Z' },
  { run_id: RUN_Q2, client_id: CID, label: 'Q2 Budget Reduction', created_at: '2026-04-15T00:00:00Z', scenario_id: SCEN_Q2 },
  { run_id: RUN_6MO, client_id: CID, label: '+6-Month Actual', created_at: '2026-07-15T00:00:00Z' },
];

export const RUN_SCORES: RunScore[] = [
  ...buildRun(RUN_Q1, 'q1'),
  ...buildRun(RUN_Q2, 'q2'),
  ...buildRun(RUN_6MO, 'm6'),
];

export const SCENARIOS: Scenario[] = [
  {
    scenario_id: SCEN_Q2,
    client_id: CID,
    name: 'Q2 Budget Reduction',
    base_run_id: RUN_Q1,
    levers: {
      weight_deltas: { financial_impact: 0.15, operational_urgency: 0.0, strategic_alignment: -0.05, implementation_feasibility: -0.10 },
      budget_cap: Q2_BUDGET_CAP, // $40.0M (base run runs at BASELINE_BUDGET_CAP = $50.0M)
      resource_pool: 18, // FTE
    },
    created_at: '2026-04-10T00:00:00Z',
  },
];

const CFO = { role: 'CFO Gate', name: 'Morgan Ellis (CFO)' };
const COMMITTEE = { role: 'Investment Committee', name: 'Priya Nair (Committee Chair)' };

export const DECISION_LOG: DecisionLogEntry[] = [
  {
    decision_id: 'dec-0001', client_id: CID, use_case_id: 1693, run_id: RUN_Q1,
    decision: 'APPROVE', rationale: 'Tier-1 value-based-care lever (Care Coordination and Transition Management); ROI defensible at the CFO gate with a 3-6 month time-to-value and 3-month payback. Advanced to committee for portfolio inclusion.',
    decided_by: CFO, decided_at: '2026-02-05T15:30:00Z', status_before: 'CANDIDATE', status_after: 'APPROVED',
  },
  {
    decision_id: 'dec-0002', client_id: CID, use_case_id: 30, run_id: RUN_Q1,
    decision: 'DEFER', rationale: 'Strongest strategic alignment but implementation feasibility (44/100) is below the delivery-capacity bar this cycle. Defer pending a data-platform readiness review.',
    decided_by: COMMITTEE, decided_at: '2026-02-05T16:10:00Z', status_before: 'SHORTLISTED', status_after: 'DEFERRED',
  },
  {
    decision_id: 'dec-0003', client_id: CID, use_case_id: 38, run_id: RUN_Q1,
    decision: 'HOLD', rationale: 'Genomic infrastructure and consent frameworks are prerequisites; revisit after the research data platform lands.',
    decided_by: COMMITTEE, decided_at: '2026-02-12T14:00:00Z', status_before: 'CANDIDATE', status_after: 'ON_HOLD',
  },
  {
    decision_id: 'dec-0004', client_id: CID, use_case_id: 31, run_id: RUN_Q2, scenario_id: SCEN_Q2,
    decision: 'DEFER', rationale: 'Under the $40M Q2 cap the capital-heavy virtual-care build ($3.0M ask) no longer fits below the funded line. Defer to FY27; keep the ADT groundwork warm.',
    decided_by: CFO, decided_at: '2026-04-18T11:20:00Z', status_before: 'APPROVED', status_after: 'DEFERRED',
  },
  {
    decision_id: 'dec-0005', client_id: CID, use_case_id: 40, run_id: RUN_Q2, scenario_id: SCEN_Q2,
    decision: 'APPROVE', rationale: 'Rises into the funded set under the financial-impact reweighting; low capital ask ($0.9M) with fast payback. Approved for the Q2 portfolio.',
    decided_by: COMMITTEE, decided_at: '2026-04-18T11:45:00Z', status_before: 'SHORTLISTED', status_after: 'APPROVED',
  },
  {
    decision_id: 'dec-0006', client_id: CID, use_case_id: 23, run_id: RUN_Q2, scenario_id: SCEN_Q2,
    decision: 'APPROVE', rationale: 'Moved up several ranks under the reweighting; balanced profile and adequate feasibility. Approved alongside the care-coordination initiative.',
    decided_by: COMMITTEE, decided_at: '2026-04-18T12:05:00Z', status_before: 'SHORTLISTED', status_after: 'APPROVED',
  },
  {
    decision_id: 'dec-0007', client_id: CID, use_case_id: 1693, run_id: RUN_Q2, scenario_id: SCEN_Q2,
    decision: 'APPROVE', rationale: 'Rises to #1 under the Q2 scenario; the CFO gate committed funding and moved it to execution ahead of the pilot readout.',
    decided_by: CFO, decided_at: '2026-04-22T09:00:00Z', status_before: 'APPROVED', status_after: 'FUNDED',
  },
  {
    decision_id: 'dec-0008', client_id: CID, use_case_id: 39, run_id: RUN_6MO,
    decision: 'KILL', rationale: '6-month actuals confirm sensor coverage and capex are not aligned; energy-optimization returns do not clear the bar. Removed from the portfolio.',
    decided_by: CFO, decided_at: '2026-07-18T10:30:00Z', status_before: 'DEFERRED', status_after: 'KILLED',
  },
];
